/**
 * Build a `{dimensionId: {labelId: labelName}}` lookup from a taxonomy
 * version's dimensions, for resolving raw label ids to human-readable
 * names. Categories are flattened — the grouping isn't needed for a flat
 * id → name map.
 *
 * Extracted from the adjudication `ComparisonPane` so the adjudication
 * flow and the datasets record-detail panel (C7-FE) share one
 * implementation. Memoised on the `dimensions` reference; callers pass
 * the array straight from
 * `useVersionDetail(...).data?.taxonomyData?.dimensions` (which is a
 * stable reference per react-query cache entry). A label id absent from
 * the map is the caller's cue to fall back to the raw id.
 */
import { useMemo } from 'react';

import type { TaxonomyDimension } from '../types/taxonomy';

export const useLabelNames = (
  dimensions: TaxonomyDimension[],
): Record<string, Record<string, string>> =>
  useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    for (const dim of dimensions) {
      const inner: Record<string, string> = {};
      for (const cat of dim.categories ?? []) {
        for (const label of cat.labels ?? []) {
          inner[label.id] = label.name;
        }
      }
      out[dim.id] = inner;
    }
    return out;
  }, [dimensions]);

export default useLabelNames;
